// New Order — focus + rail. Three columns: product list (newest-first, colourways grouped under
// their style), a focused product view (gallery + colourway picker + size run), and a running order
// rail. Reuses the existing catalog data, quantity rules, stock locking and Shopify draft
// write-back — this is purely the ordering UI. Colours are separate Shopify products sharing a
// title stem (the "style").
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import StatusBadge from '../components/StatusBadge.jsx';
import { fetchOrderingCatalog, fetchOrderPromotions, fetchProductImages, fetchShippingEstimate, orderFormUrl, inventoryUrl, uploadOrderForm } from '../api/catalog.js';
import { createOrder } from '../api/orders.js';
import { fmtMoney, fmtOrderTotal, qtyError, capCatalogForClient } from '../lib/matrix.js';
import { useAuthStore } from '../stores/authStore.js';
import { fetchMyCredit, fetchLocationCredit } from '../api/me.js';
import { safeGet, safeSet } from '../lib/storage.js';
import { promoProgress } from '../lib/promotions.js';
import { setPresenceDetail, presenceChanged } from '../lib/presence.js';

// Colourway swatches: the custom.product_hex_color metafield may carry one OR more hex codes (any
// delimiter, or a JSON list). Pull them all out and render a solid / split / multi swatch.
const parseHexes = (v) => (String(v || '').match(/#?[0-9a-fA-F]{6}\b|#?[0-9a-fA-F]{3}\b/g) || []).map((s) => (s.startsWith('#') ? s : `#${s}`));
const swatchBackground = (hexColor) => {
  const c = parseHexes(hexColor);
  if (c.length === 0) return 'var(--surface-raised,#e5e7eb)';
  if (c.length === 1) return c[0];
  if (c.length === 2) return `linear-gradient(135deg, ${c[0]} 0 50%, ${c[1]} 50% 100%)`;
  const step = 100 / c.length;
  return `conic-gradient(${c.map((h, i) => `${h} ${i * step}% ${(i + 1) * step}%`).join(', ')})`;
};

// Category filter — Shopify's real productType field (set on the product in Shopify admin).
const TYPES = [
  { key: '', label: 'All' },
  { key: 'Underwear', label: 'Underwear' },
  { key: 'Swimwear', label: 'Swimwear' },
  { key: 'Apparel', label: 'Apparel' },
  { key: 'Activewear', label: 'Activewear' }, // legacy type (replaced by Apparel 4 Nov 2026) — shown only while products still have it
  { key: 'Accessories', label: 'Accessories' },
];

const SIZE_RANK = { XS: 0, S: 1, M: 2, L: 3, XL: 4, XXL: 5, '2XL': 5, XXXL: 6, '3XL': 6, OS: 9, 'ONE SIZE': 9 };
const sizeRank = (s) => SIZE_RANK[String(s).toUpperCase().trim()] ?? 50;
const sortedVariants = (p) => [...p.variants].sort((a, b) => sizeRank(a.title) - sizeRank(b.title) || String(a.title).localeCompare(b.title));
const minVariantPrice = (p) => {
  const ps = p.variants.map((v) => v.price).filter((x) => x != null);
  return ps.length ? Math.min(...ps) : null;
};

const availDot = { in: 'text-success', low: 'text-warning', out: 'text-text-muted', unknown: 'text-text-muted' };
const availLabel = { in: 'In stock', low: 'Low', out: 'Out', unknown: '' };

// Cache of the ordering catalog per scope → revisiting a client is instant (revalidated in the
// background). Two tiers: an in-memory Map for this SPA session, and localStorage so it survives a
// full page reload too. Orders re-validate pricing/stock live at submit, so a stale browse is safe.
const catalogCache = new Map();
const LS_PREFIX = 'of:cat:';
const readPersisted = (scopeKey) => {
  try {
    const raw = localStorage.getItem(LS_PREFIX + scopeKey);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
const writePersisted = (scopeKey, data) => {
  try {
    localStorage.setItem(LS_PREFIX + scopeKey, JSON.stringify(data));
  } catch {
    // Quota exceeded — drop older cached catalogs and retry once (keep only this one).
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (k && k.startsWith(LS_PREFIX)) localStorage.removeItem(k);
      }
      localStorage.setItem(LS_PREFIX + scopeKey, JSON.stringify(data));
    } catch {
      /* give up — the in-memory cache still applies */
    }
  }
};

const scopeKeyOf = (scope) => (scope ? `${scope.store}:${scope.companyLocationId}` : 'self');

/** Warm the caches for a scope in the background so opening New Order is instant. Best-effort; skips
 *  if already warm this session. Called on app load for clients (their own catalogue). */
export async function prefetchOrderingCatalog(scope) {
  const scopeKey = scopeKeyOf(scope);
  if (catalogCache.has(scopeKey)) return;
  try {
    const d = await fetchOrderingCatalog(scope);
    catalogCache.set(scopeKey, d);
    writePersisted(scopeKey, d);
  } catch {
    /* best-effort — the normal load path will surface any real error */
  }
}

export default function OrderFlow({ scope, onBehalfOf, onBehalfLabel }) {
  const navigate = useNavigate();
  const [rawData, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // ?type= opens on a category (the home page's category tiles).
  const [typeFilter, setTypeFilter] = useState(() => new URLSearchParams(window.location.search).get('type') || '');
  // ?q= pre-fills the search (links from marketing emails land on the product).
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get('q') || '');
  const [activeStyle, setActiveStyle] = useState(null);
  const [activeColourId, setActiveColourId] = useState(null);
  const [runQty, setRunQty] = useState({}); // variantId -> qty (current focus size-run, pre-add)
  const [gallery, setGallery] = useState([]); // image urls for the active colour
  const [activeImg, setActiveImg] = useState(0);
  const imgCache = useRef(new Map());

  const [lines, setLines] = useState([]); // [{ productId, style, colour, sku, unitPrice, sizes: {variantId:{size,qty,price}} }]
  // "Reorder" hands the previous order's lines over in router state. Applied ONCE, after the catalogue
  // has loaded (we need it to resolve each variant's size/price and to know what's still orderable).
  const viewerRole = useAuthStore((st) => st.user?.role);
  // Clients are capped at what the warehouse can supply; staff/reps see the catalog uncapped.
  const data = useMemo(() => (viewerRole === 'CLIENT' ? capCatalogForClient(rawData) : rawData), [rawData, viewerRole]);
  const routerLocation = useLocation();
  const reorderRequest = routerLocation.state?.reorder;
  // merge: add to the order already in progress (home page "Add all to my order") instead of
  // replacing it; label: what was added, for the confirmation notice.
  const reorderMerge = Boolean(routerLocation.state?.merge);
  const reorderLabel = routerLocation.state?.label || null;
  const reorderApplied = useRef(false);
  const [notice, setNotice] = useState('');
  const [note, setNote] = useState('');
  const [poNumber, setPoNumber] = useState(''); // the client's own purchase-order reference
  const [step, setStep] = useState('build'); // build | review
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [result, setResult] = useState(null);
  const [shipEstimate, setShipEstimate] = useState(null);
  const [shipLoading, setShipLoading] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [editingId, setEditingId] = useState(null); // line being edited (kept in the order until saved)

  // A half-built order survives a refresh, a trip to Messages or closing the tab: it's kept per viewer
  // and per ordering location in this browser, and cleared once submitted.
  const draftKey = `wholesale.orderDraft.${viewerRole || 'user'}.${scopeKeyOf(scope)}`;
  // Saving only starts once this location's draft has been read back — otherwise the empty initial cart
  // would overwrite it before it's restored.
  const [draftReady, setDraftReady] = useState(false);
  const [draftNotice, setDraftNotice] = useState(false);
  // Restore (or reset) the order for this location — called when the catalogue for it starts loading.
  const restoreDraft = () => {
    setDraftReady(true);
    if (reorderRequest?.length && !reorderMerge) return setLines([]); // a Reorder hand-off seeds its own lines
    try {
      const d = JSON.parse(safeGet(draftKey) || 'null');
      if (d?.lines?.length) {
        setLines(d.lines);
        setNote(d.note || '');
        setPoNumber(d.poNumber || '');
        setDraftNotice(true);
        return undefined;
      }
    } catch {
      /* ignore a corrupt draft */
    }
    setLines([]);
    return undefined;
  };
  useEffect(() => {
    if (!draftReady) return;
    safeSet(draftKey, lines.length ? JSON.stringify({ lines, note, poNumber, savedAt: Date.now() }) : '');
  }, [draftReady, draftKey, lines, note, poNumber]);
  const clearDraft = () => {
    safeSet(draftKey, '');
    setLines([]);
    setNote('');
    setPoNumber('');
    setDraftNotice(false);
  };
  // A client whose account is on hold can't submit — tell them up front, not after they build a cart.
  const [hold, setHold] = useState(null);
  // Reps/staff: the client's hold status for the location they're ordering for — the order still goes
  // through, but needs a TEAMM8 approval override, so say so before they build it.
  const [clientHold, setClientHold] = useState(null);
  useEffect(() => {
    if (viewerRole === 'CLIENT') {
      fetchMyCredit().then((h) => setHold(h?.onHold ? h : null)).catch(() => setHold(null));
    } else if (scope?.companyLocationId) {
      fetchLocationCredit(scope.companyLocationId)
        .then((h) => setClientHold(h?.onHold || h?.overLimit ? h : null))
        .catch(() => setClientHold(null));
    }
  }, [viewerRole, scope?.companyLocationId]);

  const scopeKey = scopeKeyOf(scope);
  const currency = data?.catalog?.currency;
  const minOrderValue = data?.minOrderValue || 0;

  useEffect(() => {
    let alive = true;
    setError('');
    restoreDraft(); // this location's unfinished order (or an empty one)
    setActiveStyle(null);
    setActiveColourId(null);
    setRunQty({});
    // Show the cached catalogue for this client instantly if we have it (memory first, then
    // localStorage so a page reload is still instant); otherwise spin. Always revalidate in the
    // background — the active style/colour persist by name/id so it's seamless.
    const cachedData = catalogCache.get(scopeKey) || readPersisted(scopeKey);
    if (cachedData) {
      catalogCache.set(scopeKey, cachedData);
      setData(cachedData);
      setLoading(false);
    } else {
      setData(null);
      setLoading(true);
    }
    fetchOrderingCatalog(scope)
      .then((d) => {
        if (!alive) return;
        catalogCache.set(scopeKey, d);
        writePersisted(scopeKey, d);
        setData(d);
      })
      .catch((e) => {
        if (alive && !catalogCache.get(scopeKey)) setError(e.response?.data?.error?.message || 'Could not load the catalogue.');
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  // Group colour-products into styles (one left-rail entry per style stem). `data.products` already
  // arrives newest-first with colourways of a style kept adjacent, so a Map preserves that order.
  const styleGroups = useMemo(() => {
    const products = data?.products || [];
    const map = new Map();
    for (const p of products) {
      const key = p.style || p.title;
      if (!map.has(key)) map.set(key, { style: key, colours: [], type: p.productType, image: p.image });
      const g = map.get(key);
      g.colours.push(p);
      if (!g.image && p.image) g.image = p.image;
    }
    for (const g of map.values()) {
      const prices = g.colours.flatMap((c) => c.variants.map((v) => v.price)).filter((x) => x != null);
      g.priceFrom = prices.length ? Math.min(...prices) : null;
    }
    return [...map.values()];
  }, [data]);

  // Left rail: filter by type + search — newest-first order (styleGroups' order) is kept as-is.
  const railGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    return styleGroups.filter(
      (g) =>
        (!typeFilter || g.type === typeFilter) &&
        (!q || g.style.toLowerCase().includes(q) || g.colours.some((c) => c.variants.some((v) => (v.sku || '').toLowerCase().includes(q))))
    );
  }, [styleGroups, typeFilter, search]);

  const activeGroup = useMemo(() => styleGroups.find((g) => g.style === activeStyle) || null, [styleGroups, activeStyle]);
  const activeColour = useMemo(
    () => activeGroup?.colours.find((c) => c.id === activeColourId) || activeGroup?.colours[0] || null,
    [activeGroup, activeColourId]
  );

  // Default the focus to the first shown style once data loads / filters change.
  useEffect(() => {
    if (!activeGroup && railGroups.length) {
      setActiveStyle(railGroups[0].style);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [railGroups]);

  // Load the active colour's image gallery (lazy, cached).
  useEffect(() => {
    if (!activeColour) return;
    setActiveImg(0);
    const fallback = activeColour.image ? [activeColour.image] : [];
    if (imgCache.current.has(activeColour.id)) {
      setGallery(imgCache.current.get(activeColour.id));
      return;
    }
    setGallery(fallback);
    let alive = true;
    fetchProductImages(scope, activeColour.id)
      .then((imgs) => {
        const list = imgs?.length ? imgs : fallback;
        imgCache.current.set(activeColour.id, list);
        if (alive) setGallery(list);
      })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeColour?.id]);

  // Tell the live dashboard what a client is doing here (the product open, reviewing, just ordered).
  useEffect(() => {
    if (viewerRole !== 'CLIENT') return undefined;
    if (result) setPresenceDetail('Just placed an order');
    else if (step === 'review') setPresenceDetail('Reviewing their order before submitting');
    else if (activeGroup && activeColour) setPresenceDetail(`Viewing ${activeGroup.style}${activeColour.colour && activeColour.colour !== 'Default' ? ` — ${activeColour.colour}` : ''}`);
    return undefined;
  }, [viewerRole, result, step, activeGroup, activeColour]);
  useEffect(() => () => setPresenceDetail(null), []);

  // ?product=<Shopify product id> (links in marketing emails) opens that exact style + colour once the
  // catalogue is loaded. Products outside this client's catalogue fall back to the ?q= search.
  const wantedProduct = useRef(new URLSearchParams(window.location.search).get('product'));
  useEffect(() => {
    const want = wantedProduct.current;
    if (!want || !data?.products?.length) return;
    const p = data.products.find((x) => x.id === want || String(x.id).endsWith(`/${want}`));
    if (p) {
      setSearch('');
      setActiveStyle(p.style || p.title);
      setActiveColourId(p.id);
    }
    wantedProduct.current = null;
  }, [data]);

  // Products a rep was told to pitch to this client (rep home → "Pitch these"), shown as a strip that
  // opens each one. Only those in this client's catalogue are shown.
  const suggested = useMemo(() => {
    const want = routerLocation.state?.suggest || [];
    if (!want.length || !data?.products?.length) return [];
    return want.map((w) => data.products.find((p) => p.id === w.productId)).filter(Boolean);
  }, [routerLocation.state, data]);
  const openProduct = (p) => {
    setSearch('');
    setTypeFilter('');
    setActiveStyle(p.style || p.title);
    setActiveColourId(p.id);
    setRunQty({});
  };

  const selectStyle = (style) => { setActiveStyle(style); setActiveColourId(null); setRunQty({}); };
  const selectColour = (id) => { setActiveColourId(id); setRunQty({}); };
  const setCell = (variantId, value) => {
    const n = value === '' ? 0 : Math.max(0, Math.floor(Number(value)));
    setRunQty((prev) => { const next = { ...prev }; if (!n) delete next[variantId]; else next[variantId] = n; return next; });
  };

  const runUnits = Object.values(runQty).reduce((a, b) => a + b, 0);
  const runInvalid = useMemo(() => {
    if (!activeColour) return false;
    return activeColour.variants.some((v) => runQty[v.id] && qtyError(runQty[v.id], v.quantityRule));
  }, [runQty, activeColour]);

  const addToOrder = () => {
    if (!activeColour || !runUnits || runInvalid) return;
    const sizes = {};
    for (const v of activeColour.variants) {
      const n = runQty[v.id] || 0;
      if (n > 0) sizes[v.id] = { size: v.title, qty: n, price: v.price };
    }
    const editing = editingId === activeColour.id;
    setLines((prev) => {
      const existing = prev.find((l) => l.productId === activeColour.id);
      if (existing && editing) {
        // Editing a line: the run REPLACES it (removing every size empties the line).
        return Object.keys(sizes).length ? prev.map((l) => (l.productId === activeColour.id ? { ...l, sizes } : l)) : prev.filter((l) => l.productId !== activeColour.id);
      }
      if (existing) {
        const merged = { ...existing.sizes };
        for (const [vid, s] of Object.entries(sizes)) merged[vid] = { ...s, qty: (merged[vid]?.qty || 0) + s.qty };
        // Topping up a line can't take a size past what a client can order — hold it at the cap.
        const capped = [];
        for (const v of activeColour.variants) {
          const cap = v.quantityRule?.stockMax;
          if (cap != null && merged[v.id]?.qty > cap) {
            merged[v.id] = { ...merged[v.id], qty: cap };
            capped.push(`${v.title} (${cap})`);
          }
        }
        if (capped.length) setNotice(`Only so many are available, so we held these sizes at the most you can order: ${capped.join(', ')}.`);
        return prev.map((l) => (l.productId === activeColour.id ? { ...l, sizes: merged } : l));
      }
      return [
        ...prev,
        { productId: activeColour.id, style: activeGroup.style, colour: activeColour.colour || 'Default', sku: activeColour.variants.find((v) => v.sku)?.sku || '', sizes },
      ];
    });
    setRunQty({});
    setEditingId(null);
  };

  // Seed the order from a "Reorder" hand-off. Runs once the catalogue is in, matching each previous
  // variant to what the client can order TODAY — a style that's been discontinued or is out of stock is
  // reported rather than silently dropped. (Before this, the payload was passed but never read, so
  // Reorder just opened an empty order page.)
  useEffect(() => {
    if (reorderApplied.current || !data?.products?.length || !reorderRequest?.length) return;
    reorderApplied.current = true;
    const want = new Map(reorderRequest.map((l) => [l.variantId, Number(l.quantity) || 0]).filter(([, q]) => q > 0));
    if (!want.size) return;
    const seeded = [];
    let added = 0;
    let unavailable = 0;
    let reduced = 0; // sizes cut back to what's available (clients only)
    for (const p of data.products) {
      const sizes = {};
      for (const v of p.variants || []) {
        const q = want.get(v.id);
        if (!q) continue;
        want.delete(v.id);
        if (v.locked || v.price == null) { unavailable += 1; continue; }
        const cap = v.quantityRule?.stockMax;
        if (cap != null && q > cap) reduced += 1;
        sizes[v.id] = { size: v.title, qty: cap != null ? Math.min(q, cap) : q, price: v.price };
        added += 1;
      }
      if (Object.keys(sizes).length) {
        seeded.push({
          productId: p.id,
          style: p.style,
          colour: p.colour || 'Default',
          sku: (p.variants || []).find((v) => v.sku)?.sku || '',
          sizes,
        });
      }
    }
    const missing = unavailable + want.size; // out of stock/locked, plus no longer in the catalogue
    if (seeded.length && reorderMerge) {
      // Merge into the order in progress: each size ends up at least the suggested quantity (so
      // pressing "Add all" twice never doubles it), anything already in the order stays.
      setLines((prev) => {
        const next = prev.map((l) => ({ ...l, sizes: { ...l.sizes } }));
        for (const s of seeded) {
          const have = next.find((l) => l.productId === s.productId);
          if (!have) next.push(s);
          else for (const [vid, sz] of Object.entries(s.sizes)) have.sizes[vid] = { ...sz, qty: Math.max(sz.qty, have.sizes[vid]?.qty || 0) };
        }
        return next;
      });
      setNotice(
        added === 0
          ? 'None of those items are available to order right now.'
          : `${reorderLabel || 'Your restock list'} added to your order — ${added} size${added === 1 ? '' : 's'}${missing ? `, ${missing} unavailable and left out` : ''}.${reduced ? ` ${reduced} size${reduced === 1 ? ' was' : 's were'} reduced to what's in stock.` : ''} Adjust anything, then review.`
      );
      return;
    }
    if (seeded.length) setLines(seeded);
    setNotice(
      (missing === 0
        ? `Previous order loaded — ${added} size${added === 1 ? '' : 's'} added. Adjust quantities and submit.`
        : added === 0
          ? "None of that order's items are available to order right now."
          : `Previous order loaded — ${added} size${added === 1 ? '' : 's'} added, ${missing} unavailable and left out.`) +
        (reduced ? ` ${reduced} size${reduced === 1 ? ' was' : 's were'} reduced to what's in stock.` : '')
    );
  }, [data, reorderRequest, reorderMerge, reorderLabel]);

  const removeLine = (productId) => setLines((prev) => prev.filter((l) => l.productId !== productId));
  // Edit: load the line into the size run. It STAYS in the order until "Update line" — picking another
  // product mid-edit no longer loses it.
  const editLine = (line) => {
    selectStyle(line.style);
    setActiveColourId(line.productId);
    setRunQty(Object.fromEntries(Object.entries(line.sizes).map(([vid, s]) => [vid, s.qty])));
    setEditingId(line.productId);
  };

  const totals = useMemo(() => {
    let units = 0;
    let subtotal = 0;
    for (const l of lines) for (const s of Object.values(l.sizes)) { units += s.qty; subtotal += s.qty * (s.price || 0); }
    return { units, subtotal: Math.round(subtotal * 100) / 100 };
  }, [lines]);
  // Tell the live dashboard about a client's order in progress as it changes (the heartbeat reads it from the saved draft).
  useEffect(() => {
    if (viewerRole === 'CLIENT' && draftReady) presenceChanged();
  }, [viewerRole, draftReady, totals.units, totals.subtotal]);
  const minMet = !minOrderValue || totals.subtotal >= minOrderValue;

  // Live promotions for this client, in the order currency → "add X more for Y% off" nudge.
  const [promos, setPromos] = useState([]);
  useEffect(() => {
    if (!currency) return undefined;
    let alive = true;
    fetchOrderPromotions(scope, currency)
      .then((r) => alive && setPromos(r.promotions || []))
      .catch(() => alive && setPromos([]));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey, currency]);
  const promo = useMemo(() => promoProgress(promos, totals.subtotal), [promos, totals.subtotal]);
  const promoDiscount = promo.current ? promo.current.saving : 0;

  // Shipping estimate: computed once on entering review (not per rail change — avoids hammering it).
  useEffect(() => {
    if (step !== 'review') return;
    let alive = true;
    setShipLoading(true);
    setShipEstimate(null);
    const estimateLines = lines.flatMap((l) =>
      Object.entries(l.sizes).filter(([, s]) => s.qty > 0).map(([variantId, s]) => ({ variantId, quantity: s.qty }))
    );
    fetchShippingEstimate(scope, { units: totals.units, value: totals.subtotal, lines: estimateLines })
      .then((e) => alive && setShipEstimate(e))
      .catch(() => alive && setShipEstimate({ error: true }))
      .finally(() => alive && setShipLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const submit = async () => {
    setSubmitError('');
    setSubmitting(true);
    try {
      const payloadLines = lines.flatMap((l) => Object.entries(l.sizes).filter(([, s]) => s.qty > 0).map(([variantId, s]) => ({ variantId, quantity: s.qty })));
      const payload = { lines: payloadLines, ...(note.trim() ? { note: note.trim() } : {}), ...(poNumber.trim() ? { poNumber: poNumber.trim() } : {}) };
      if (scope) Object.assign(payload, scope);
      if (onBehalfOf) payload.onBehalfOfClientUserId = onBehalfOf;
      setResult(await createOrder(payload));
      safeSet(draftKey, '');
    } catch (e) {
      const err = e.response?.data?.error;
      const lineErrs = err?.details?.lineErrors;
      if (lineErrs?.length) {
        // Say WHICH item and what to do, not "Some lines are invalid (2 line issues)".
        const nameOf = (variantId) => {
          for (const l of lines) if (l.sizes[variantId]) return `${l.style} · ${l.colour} · ${l.sizes[variantId].size}`;
          return 'An item';
        };
        setSubmitError(['Some items need changing before we can submit:', ...lineErrs.map((le) => `• ${nameOf(le.variantId)}: ${le.error}`)].join('\n'));
      } else {
        setSubmitError(err?.message || 'Could not submit the order — please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Upload a filled Excel order form → creates the order against this same scope.
  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm(`Submit an order from “${file.name}”?\n\nEvery row with a quantity is ordered straight away and sent to TEAMM8 for review — there's no separate review step for uploaded forms.`)) {
      e.target.value = '';
      return;
    }
    setUploadBusy(true);
    setUploadError('');
    try {
      const params = { ...(scope || {}), ...(onBehalfOf ? { onBehalfOfClientUserId: onBehalfOf } : {}) };
      const { order, skipped } = await uploadOrderForm(params, file);
      setResult({ ...order, skippedCount: skipped?.length || 0 });
    } catch (err) {
      setUploadError(err.response?.data?.error?.message || 'Could not import the order form.');
    } finally {
      setUploadBusy(false);
      e.target.value = '';
    }
  };

  if (loading) return <div className="text-sm text-text-muted">Loading catalogue…</div>;
  if (error) return <div className="rounded-lg border border-line bg-surface p-8 text-center text-sm text-text-muted">{error}</div>;
  // Two very different situations used to share one message — and that message was an instruction to
  // TEAMM8 staff ("Assign a pricing catalogue in Shopify first") shown to the CUSTOMER, about a system
  // they can't log into. Split them, and speak to whoever is actually looking.
  if (data?.error === 'NO_ACTIVE_CATALOG' || !data?.products?.length) {
    const noCatalogue = data?.error === 'NO_ACTIVE_CATALOG';
    const isClient = viewerRole === 'CLIENT';
    return (
      <div className="rounded-lg border border-dashed border-line bg-surface p-10 text-center text-sm text-text-muted">
        {isClient ? (
          <>
            <p className="text-text-primary">
              {noCatalogue ? 'Your range is still being set up.' : 'Nothing is available to order right now.'}
            </p>
            <p className="mt-1">
              {noCatalogue
                ? 'Our team is setting up your range — nothing is wrong with your account. Message us if you need it sooner.'
                : 'This is usually temporary while stock is replenished. Please check back shortly.'}
            </p>
            <Link to="/messages" className="mt-4 inline-block rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-hover">
              Message TEAMM8
            </Link>
          </>
        ) : noCatalogue ? (
          'No catalogue is assigned to this client yet, so there are no products to order. Assign a pricing catalogue in Shopify first.'
        ) : (
          "This client's catalogue has no orderable products right now — everything is out of stock or below the B2B stock minimum."
        )}
      </div>
    );
  }

  // ---- Order submitted ----------------------------------------------------
  if (result) {
    return (
      <div className="mx-auto max-w-lg rounded-lg border border-line bg-surface p-6 shadow-sm">
        <div className="flex items-center gap-3">
          <StatusBadge status={result.status} />
          <span className="text-sm font-semibold text-text-primary">Order received — thank you!</span>
        </div>
        <p className="mt-2 text-sm text-text-secondary">
          {result.status === 'APPROVED'
            ? 'It’s confirmed straight away. Your invoice and payment link are on their way by email.'
            : viewerRole === 'CLIENT'
              ? 'We’ll review it and confirm shipping, then email your invoice and payment link — usually within one business day. You can follow it on your orders page.'
              : 'TEAMM8 will review it and confirm shipping, then the client gets the invoice and payment link by email.'}
        </p>
        <dl className="mt-4 grid grid-cols-[8rem_1fr] gap-y-1.5 text-sm">
          {result.orderNumber && (<><dt className="text-text-muted">Order</dt><dd className="font-mono font-medium text-text-primary">{result.orderNumber}</dd></>)}
          <dt className="text-text-muted">Total</dt><dd className="font-medium text-text-primary">{fmtOrderTotal(result)}</dd>
          <dt className="text-text-muted">Units</dt><dd className="text-text-primary">{result.unitCount}</dd>
        </dl>
        {result.skippedCount > 0 && (
          <p className="mt-3 text-xs text-warning">{result.skippedCount} row(s) from the uploaded form were skipped (SKU not found or not orderable).</p>
        )}
        <div className="mt-5 flex gap-2">
          <button onClick={() => navigate(`/orders/${result.id}`)} className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover">View order</button>
          <button onClick={() => { setResult(null); setLines([]); setNote(''); setPoNumber(''); setStep('build'); }} className="rounded-md border border-line px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-raised">Place another order</button>
        </div>
      </div>
    );
  }

  // ---- Review step --------------------------------------------------------
  if (step === 'review') {
    const shipCost = shipEstimate && !shipEstimate.error ? shipEstimate.shipping : 0;
    const reviewTotal = totals.subtotal - promoDiscount + shipCost;
    return (
      <div className="mx-auto max-w-2xl">
        <button onClick={() => setStep('build')} className="mb-3 text-sm text-text-secondary hover:text-text-primary">← Back to order</button>
        <div className="rounded-lg border border-line bg-surface p-5 shadow-sm">
          <h2 className="text-base font-semibold text-text-primary">Review order{data.companyName ? ` · ${data.companyName}` : ''}</h2>
          <p className="mt-0.5 text-xs text-text-muted">{data.catalog?.title} · {currency}{data.locationName ? ` · ${data.locationName}` : ''}</p>

          <div className="mt-4 divide-y divide-line">
            {lines.map((l) => {
              const u = Object.values(l.sizes).reduce((a, s) => a + s.qty, 0);
              const t = Object.values(l.sizes).reduce((a, s) => a + s.qty * (s.price || 0), 0);
              return (
                <div key={l.productId} className="flex items-start justify-between gap-3 py-2 text-sm">
                  <div className="min-w-0">
                    <div className="font-medium text-text-primary">{l.style} · {l.colour}</div>
                    <div className="text-xs text-text-muted">{Object.values(l.sizes).sort((a, b) => sizeRank(a.size) - sizeRank(b.size)).map((s) => `${s.size}·${s.qty}`).join('  ')} · {u} units</div>
                  </div>
                  <div className="shrink-0 font-medium tabular-nums text-text-primary">{fmtMoney(t, currency)}</div>
                </div>
              );
            })}
          </div>

          <dl className="mt-4 space-y-1.5 border-t border-line pt-3 text-sm">
            <div className="flex justify-between"><dt className="text-text-muted">Units</dt><dd className="tabular-nums">{totals.units}</dd></div>
            <div className="flex justify-between"><dt className="text-text-muted">Goods</dt><dd className="tabular-nums">{fmtMoney(totals.subtotal, currency)}</dd></div>
            {promo.current && (
              <div className="flex justify-between gap-4">
                <dt className="text-success">Promotion · {promo.current.name} ({promo.current.percent}% off)</dt>
                <dd className="shrink-0 tabular-nums text-success">−{fmtMoney(promoDiscount, currency)}</dd>
              </div>
            )}
            <div className="flex justify-between gap-4">
              <dt className="text-text-muted">Shipping</dt>
              <dd className="text-right">
                {shipLoading ? (
                  <span className="text-text-muted">Calculating…</span>
                ) : shipEstimate?.error ? (
                  <span className="text-xs text-text-muted">Worked out after you submit — our team confirms it</span>
                ) : shipEstimate ? (
                  <>
                    <span className="tabular-nums text-text-primary">{shipEstimate.shipping > 0 ? fmtMoney(shipEstimate.shipping, currency) : 'Free'}</span>
                    <div className="text-[11px] text-text-muted">
                      {`Estimate${shipEstimate.destinationCountry ? ` to ${shipEstimate.destinationCountry}` : ''} — our team confirms the final shipping`}
                    </div>
                    {shipEstimate.dutiesOnArrival && (
                      <div className="mt-1 text-[11px] font-medium text-warning">
                        Import duties &amp; taxes are not included — they&apos;ll be charged on arrival of the goods.
                      </div>
                    )}
                  </>
                ) : '—'}
              </dd>
            </div>
            <div className="flex justify-between border-t border-line pt-2 text-base font-semibold text-text-primary"><dt>Estimated total</dt><dd className="tabular-nums">{fmtMoney(reviewTotal, currency)}</dd></div>
          </dl>

          <div className="mt-4 grid gap-2 sm:grid-cols-[12rem_1fr]">
            <input value={poNumber} onChange={(e) => setPoNumber(e.target.value)} maxLength={60} placeholder={viewerRole === 'CLIENT' ? 'Your PO number (optional)' : 'Client’s PO number (optional)'} aria-label="PO number" className="w-full rounded-md border border-line px-3 py-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30" />
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note (optional)" aria-label="Order note" className="w-full rounded-md border border-line px-3 py-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30" />
          </div>
          {submitError && <p className="mt-2 whitespace-pre-line text-sm text-error">{submitError}</p>}
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => setStep('build')} className="rounded-md border border-line px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-raised">Keep editing</button>
            <button onClick={submit} disabled={submitting || !lines.length || Boolean(hold)} className="rounded-md bg-accent px-5 py-2 text-sm font-semibold text-white hover:bg-accent-hover disabled:opacity-50">{submitting ? 'Submitting…' : 'Confirm & submit'}</button>
          </div>
        </div>
      </div>
    );
  }

  // ---- Build step (three columns) -----------------------------------------
  return (
    <div className="flex flex-col">
      {hold && (
        <div className="mb-3 rounded-md border border-error/40 bg-error-light px-3 py-2 text-sm text-text-primary">
          <strong className="text-error">Account on hold.</strong> {hold.message}
          {hold.overdue?.length > 0 && (
            <span className="ml-1">
              {[...new Map(hold.overdue.map((o) => [o.orderId, o])).values()].map((o, i) => (
                <Link key={o.orderId} to={`/orders/${o.orderId}`} className="font-medium text-accent hover:underline">
                  {i ? ', ' : ''}Pay {o.orderNumber}
                </Link>
              ))}
            </span>
          )}
        </div>
      )}
      {clientHold && (
        <div className="mb-3 rounded-md border border-warning/50 bg-warning-light px-3 py-2 text-sm text-text-primary">
          <strong>{onBehalfLabel || 'This client'} is {clientHold.onHold ? 'on hold' : 'over their credit limit'}.</strong> {clientHold.reasons?.join('; ')}. You can still place the order, but TEAMM8 must approve it with an override — chasing the overdue payment first will speed it up.
        </div>
      )}
      {/* Header: type filter (+ on-behalf badge) */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {TYPES.filter((t) => t.key !== 'Activewear' || styleGroups.some((g) => g.type === 'Activewear')).map((t) => (
          <button
            key={t.key}
            onClick={() => setTypeFilter(t.key)}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition ${typeFilter === t.key ? 'border-accent bg-accent text-white' : 'border-line bg-surface text-text-secondary hover:border-text-secondary hover:text-text-primary'}`}
          >
            {t.label}
          </button>
        ))}
        {onBehalfLabel && viewerRole !== 'CLIENT' && <span className="rounded-full bg-accent-light px-2.5 py-1 text-xs font-medium text-text-primary">Ordering for {onBehalfLabel}</span>}
        {/* Order-form download / upload — an alternative to picking products on screen. */}
        <div className="ml-auto flex items-center gap-2">
          <a
            href={orderFormUrl(scope)}
            className="rounded-md border border-line px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-raised"
            title="Download an Excel order form for this catalogue — fill in quantities and upload it to create the order"
          >
            Download order form
          </a>
          <a
            href={inventoryUrl(scope)}
            className="rounded-md border border-line px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-raised"
            title="Download current stock available to order at the warehouse (Excel)"
          >
            Download inventory
          </a>
          <label className="cursor-pointer rounded-md border border-line px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-raised">
            {uploadBusy ? 'Submitting…' : 'Upload & submit form'}
            <input type="file" accept=".xlsx" className="hidden" onChange={onUpload} disabled={uploadBusy} />
          </label>
        </div>
      </div>
      {uploadError && <p className="mb-3 text-sm text-error">{uploadError}</p>}
      {draftNotice && lines.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-md border border-accent/40 bg-accent-light px-3 py-2 text-sm text-text-primary">
          <span>We kept your unfinished order from last time ({lines.length} item{lines.length === 1 ? '' : 's'}).</span>
          <span className="flex gap-3">
            <button onClick={() => setDraftNotice(false)} className="text-xs font-medium text-accent hover:underline">
              Continue
            </button>
            <button onClick={clearDraft} className="text-xs text-text-muted hover:text-text-primary hover:underline">
              Start fresh
            </button>
          </span>
        </div>
      )}
      {suggested.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-line bg-surface px-3 py-2 text-sm">
          <span className="font-semibold text-text-primary">Pitch on this call:</span>
          {suggested.map((p) => (
            <button key={p.id} type="button" onClick={() => openProduct(p)} className={`rounded-full border px-3 py-1 text-[13px] ${activeColour?.id === p.id ? 'border-accent bg-accent text-white' : 'border-line-strong text-text-primary hover:bg-surface-raised'}`}>
              {p.title}
            </button>
          ))}
        </div>
      )}
      {notice && (
        <div className="mb-3 flex items-start justify-between gap-3 rounded-md border border-accent/40 bg-accent-light px-3 py-2 text-sm text-text-primary">
          <span>{notice}</span>
          <button onClick={() => setNotice('')} className="text-xs text-text-muted hover:text-text-primary" aria-label="Dismiss">
            Dismiss
          </button>
        </div>
      )}

      {/* Large screens: three fixed-height columns. Smaller screens: the page scrolls normally (product
          list capped, then the product, then the order) with a sticky "Review order" bar at the bottom. */}
      <div className="grid grid-cols-1 gap-3 pb-20 lg:h-[calc(100vh-11rem)] lg:min-h-[540px] lg:grid-cols-[340px_1fr_320px] lg:pb-0">
        {/* LEFT: product list, newest first (colourways grouped under their style) */}
        <div className="flex max-h-[45vh] flex-col overflow-hidden rounded-lg border border-line bg-surface lg:max-h-none">
          <div className="border-b border-line p-2">
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="w-full rounded-md border border-line px-2.5 py-1.5 text-sm outline-none focus:border-accent" />
          </div>
          <div className="flex-1 overflow-auto p-1.5">
            {railGroups.length === 0 ? (
              <p className="p-6 text-center text-xs text-text-muted">No products in this type.</p>
            ) : (
              railGroups.map((g) => (
                <button
                  key={g.style}
                  onClick={() => selectStyle(g.style)}
                  className={`flex w-full items-center gap-3 rounded-md p-2 text-left transition ${g.style === activeStyle ? 'bg-accent-light' : 'hover:bg-surface-raised'}`}
                >
                  {g.image ? <img src={g.image} alt="" loading="lazy" decoding="async" className="h-16 w-14 shrink-0 rounded object-cover" /> : <span className="h-16 w-14 shrink-0 rounded bg-surface-raised" />}
                  <span className="min-w-0">
                    <span className="block truncate text-[13px] font-medium text-text-primary">{g.style}</span>
                    <span className="block text-[11px] text-text-muted">{g.priceFrom != null ? fmtMoney(g.priceFrom, currency) : ''}{g.colours.length > 1 ? ` · ${g.colours.length} colours` : ''}</span>
                  </span>
                </button>
              ))
            )}
          </div>
        </div>

        {/* MIDDLE: focus view */}
        <div className="overflow-auto rounded-lg border border-line bg-surface p-4">
          {!activeColour ? (
            <p className="p-10 text-center text-sm text-text-muted">Pick a product from the left to start.</p>
          ) : (
            <>
              <div className="flex flex-col gap-5 sm:flex-row">
                <div className="sm:w-[280px] sm:shrink-0">
                  <div className="aspect-[4/5] overflow-hidden rounded-xl bg-surface-raised">
                    {gallery[activeImg] ? <img src={gallery[activeImg]} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-xs text-text-muted">No image</div>}
                  </div>
                  {gallery.length > 1 && (
                    <div className="mt-2 flex gap-2">
                      {gallery.slice(0, 6).map((src, i) => (
                        <button key={i} onClick={() => setActiveImg(i)} className={`aspect-square flex-1 overflow-hidden rounded-md border-2 ${i === activeImg ? 'border-accent' : 'border-transparent'}`}>
                          <img src={src} alt="" className="h-full w-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <h2 className="text-xl font-semibold text-text-primary">{activeGroup.style}</h2>
                  <div className="mt-0.5 text-xs text-text-muted">{activeColour.productType || activeGroup.type}</div>
                  <div className="mt-3 text-sm">
                    Wholesale <span className="text-lg font-semibold text-text-primary">{minVariantPrice(activeColour) != null ? fmtMoney(minVariantPrice(activeColour), currency) : '—'}</span>
                    {activeColour.variants.find((v) => v.rrp)?.rrp ? <span className="text-text-muted"> · RRP {fmtMoney(activeColour.variants.find((v) => v.rrp).rrp, currency)}</span> : null}
                  </div>

                  {activeGroup.colours.length > 1 && (
                    <div className="mt-4">
                      <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-text-muted">Colourway</div>
                      <div className="grid grid-cols-[repeat(auto-fill,minmax(44px,1fr))] gap-x-2 gap-y-2.5">
                        {activeGroup.colours.map((c) => {
                          const on = c.id === activeColour.id;
                          return (
                            <button key={c.id} onClick={() => selectColour(c.id)} title={c.colour || 'Default'} className="flex flex-col items-center gap-1">
                              <span
                                className={`h-6 w-6 rounded-full border border-black/10 transition ${on ? 'ring-2 ring-accent ring-offset-1' : 'hover:ring-2 hover:ring-line'}`}
                                style={{ background: swatchBackground(c.hexColor) }}
                              />
                              <span className={`w-full truncate text-center text-[11px] leading-tight ${on ? 'font-medium text-text-primary' : 'text-text-muted'}`}>
                                {c.colour || 'Default'}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Size run */}
              <div className="mt-5 rounded-xl border border-line p-4">
                <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-text-muted">Size run — {activeColour.colour || 'Default'}</div>
                {/* Pack/MOQ rules existed only in a hover tooltip, so on a phone they were invisible until
                    the order was rejected. State them up front. Rules are per-variant but identical
                    across a size run in practice, so describe the run from the first orderable size. */}
                {(() => {
                  const r = sortedVariants(activeColour).find((v) => !v.locked)?.quantityRule;
                  if (!r) return null;
                  const parts = [];
                  if (r.min > 1) parts.push(`minimum ${r.min} per size`);
                  if (r.increment > 1) parts.push(`in multiples of ${r.increment}`);
                  if (r.max) parts.push(`max ${r.max}`);
                  return parts.length ? <div className="mb-3 text-[11px] text-text-secondary">Ordered {parts.join(' · ')}.</div> : <div className="mb-3" />;
                })()}
                <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
                  {sortedVariants(activeColour).map((v) => {
                    const q = runQty[v.id] || '';
                    const err = qtyError(q, v.quantityRule);
                    return (
                      <label key={v.id} className="flex flex-col items-center gap-1">
                        <span className="text-[11px] text-text-muted">{v.title}</span>
                        <input
                          type="number" min={0} max={v.quantityRule.stockMax ?? undefined} inputMode="numeric" disabled={v.locked} value={q}
                          onChange={(e) => setCell(v.id, e.target.value)}
                          aria-label={`Quantity for size ${v.title}`}
                          title={v.locked ? 'Sold out' : `Minimum ${v.quantityRule.min}${v.quantityRule.increment > 1 ? ` · multiples of ${v.quantityRule.increment}` : ''}${v.quantityRule.max ? ` · max ${v.quantityRule.max}` : ''}`}
                          className={`h-10 w-full rounded-md border text-center text-sm outline-none focus:ring-2 focus:ring-accent/30 disabled:bg-surface-raised disabled:text-text-muted ${err ? 'border-error text-error' : 'border-line text-text-primary'}`}
                        />
                        {/* The reason was already computed and used only to paint the box red — say it. */}
                        <span className={`text-[11px] leading-tight ${err ? 'font-medium text-error' : v.locked ? 'text-text-muted' : availDot[v.availability] || 'text-text-muted'}`}>
                          {err ? err : v.locked ? 'Sold out' : v.quantityRule.stockMax != null ? `${v.quantityRule.stockMax} available` : availLabel[v.availability] || ''}
                        </span>
                      </label>
                    );
                  })}
                </div>
                <div className="sticky bottom-0 -mx-4 -mb-4 mt-3 flex items-center gap-3 rounded-b-xl border-t border-line bg-surface px-4 py-3">
                  <span className="text-sm text-text-muted">{runUnits} units</span>
                  <button
                    onClick={addToOrder}
                    disabled={!runUnits || runInvalid}
                    className="flex-1 rounded-md bg-accent px-4 py-2.5 text-sm font-medium text-white transition hover:bg-accent-hover disabled:opacity-40"
                  >
                    {runInvalid ? 'Adjust the sizes marked in red' : editingId === activeColour.id ? 'Update line' : 'Add to order'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* RIGHT: running order rail */}
        <div className="flex flex-col overflow-hidden rounded-lg border border-line bg-surface">
          <div className="flex items-center border-b border-line px-4 py-3 text-sm font-semibold text-text-primary">
            Order<span className="ml-auto text-xs font-normal text-text-muted">{lines.length} line{lines.length === 1 ? '' : 's'}</span>
          </div>
          <div className="flex-1 overflow-auto">
            {lines.length === 0 ? (
              <div className="px-4 py-10 text-center text-xs text-text-muted">Nothing yet. Choose a product, enter a quantity for each size, then press “Add to order”.</div>
            ) : (
              lines.map((l) => {
                const u = Object.values(l.sizes).reduce((a, s) => a + s.qty, 0);
                const t = Object.values(l.sizes).reduce((a, s) => a + s.qty * (s.price || 0), 0);
                return (
                  <div key={l.productId} className="border-b border-line px-4 py-2.5">
                    <div className="flex items-start justify-between gap-2 text-sm font-medium text-text-primary">
                      <span className="min-w-0 truncate">{l.style} · {l.colour}</span>
                      <span className="shrink-0 tabular-nums">{fmtMoney(t, currency)}</span>
                    </div>
                    <div className="mt-0.5 text-[11.5px] text-text-muted">{Object.values(l.sizes).sort((a, b) => sizeRank(a.size) - sizeRank(b.size)).map((s) => `${s.size}·${s.qty}`).join('  ')} · {u} u</div>
                    <div className="mt-1 flex gap-3">
                      <button onClick={() => editLine(l)} className="text-[11px] font-medium text-accent hover:underline">Edit</button>
                      <button onClick={() => removeLine(l.productId)} className="text-[11px] text-error hover:underline">Remove</button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <div className="border-t border-line px-4 py-3">
            <div className="mb-1 flex justify-between text-sm text-text-muted"><span>Units</span><span className="tabular-nums">{totals.units}</span></div>
            {minOrderValue > 0 && (
              <div className="mt-2">
                <div className="h-1.5 overflow-hidden rounded-full bg-surface-raised">
                  <div className="h-full rounded-full bg-success transition-all" style={{ width: `${Math.min(100, (totals.subtotal / minOrderValue) * 100)}%` }} />
                </div>
                <div className="mt-1 text-[11px] text-text-muted">
                  {minMet ? 'Minimum order met ✓' : `Minimum ${fmtMoney(minOrderValue, currency)} — add ${fmtMoney(minOrderValue - totals.subtotal, currency)}`}
                </div>
              </div>
            )}
            <PromoNudge promo={promo} subtotal={totals.subtotal} currency={currency} />
            <div className="mt-1.5 flex justify-between text-base font-semibold text-text-primary"><span>Goods subtotal</span><span className="tabular-nums">{fmtMoney(totals.subtotal, currency)}</span></div>
            <p className="mt-0.5 text-[11px] text-text-muted">Shipping is estimated on the next step and confirmed by our team.</p>
            <button
              onClick={() => setStep('review')}
              disabled={!lines.length || !minMet}
              className="mt-3 w-full rounded-md bg-accent px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-accent-hover disabled:opacity-40"
            >
              Review order
            </button>
          </div>
        </div>
      </div>
      {/* Small screens: always-visible order total + Review, so the cart never scrolls out of reach. */}
      {lines.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 flex items-center gap-3 border-t border-line bg-surface px-4 py-3 shadow-lg lg:hidden">
          <div className="min-w-0 flex-1 text-sm">
            <div className="font-semibold text-text-primary">
              {totals.units} units · {fmtMoney(totals.subtotal, currency)}
            </div>
            {!minMet ? (
              <div className="text-[11px] text-text-muted">Minimum {fmtMoney(minOrderValue, currency)}</div>
            ) : promo.next ? (
              <div className="truncate text-[11px] font-medium text-accent">Add {fmtMoney(promo.next.gap, currency)} for {promo.next.percent}% off</div>
            ) : promo.current ? (
              <div className="text-[11px] font-medium text-success">{promo.current.percent}% promotion applied</div>
            ) : null}
          </div>
          <button
            onClick={() => setStep('review')}
            disabled={!minMet}
            className="rounded-md bg-accent px-4 py-2.5 text-sm font-semibold text-white hover:bg-accent-hover disabled:opacity-40"
          >
            Review order
          </button>
        </div>
      )}
    </div>
  );
}

// Cart promotion nudge: what the live promotion saves now, and how much more reaches the next tier.
function PromoNudge({ promo, subtotal, currency }) {
  const { current, next } = promo;
  if (!current && !next) return null;
  const pct = next ? Math.min(100, Math.max(0, ((subtotal - next.from) / (next.min - next.from)) * 100)) : 100;
  return (
    <div className="mt-2 rounded-md border border-accent/30 bg-accent-light px-3 py-2">
      {current && (
        <div className="flex justify-between gap-2 text-xs font-medium text-success">
          <span className="min-w-0 truncate">{current.percent}% off · {current.name}</span>
          <span className="shrink-0 tabular-nums">−{fmtMoney(current.saving, currency)}</span>
        </div>
      )}
      {next && (
        <>
          <div className={`text-xs text-text-primary ${current ? 'mt-1.5' : ''}`}>
            Add <strong className="tabular-nums">{fmtMoney(next.gap, currency)}</strong> more to get <strong>{next.percent}% off</strong> your whole order
            {!current && <span className="text-text-muted"> · {next.name}</span>}
          </div>
          <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-surface">
            <div className="h-full rounded-full bg-accent transition-all" style={{ width: `${pct}%` }} />
          </div>
        </>
      )}
      {current?.tiered && !next && <div className="mt-1 text-[11px] text-text-muted">You’ve reached the top tier.</div>}
    </div>
  );
}
